import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import dayjs from "dayjs";
import { getProvinceOptionList, toProv } from "../../components/Analytics/PriceReports/provinceUtils";
import { fetchProductsByIds } from "../../services/beefAiPricing";
import {
  fetchFlyerGalleryPage,
  fetchFlyerOfferCountByProvince,
  fetchFlyerOfferCountByRetailerAndDiscountType,
  fetchFlyerPromotionalFrequency,
  fetchFlyerReportDistinctFilterOptions,
  fetchFlyerReportSummaryStats,
} from "../../services/beefAiFlyerReport";
import {
  buildFlyerPromotionalFrequencyLineChart,
  buildFlyerProvinceOfferCountChart,
  buildFlyerRetailerStackedBarChart,
} from "./flyerReportChartUtils";
import { mapFlyerGalleryRowsToDisplayItems } from "./flyerGalleryDisplay";

export const flyerReportSwrOptions = { revalidateOnFocus: false };

export function useFlyerReportPanelState({ queriesEnabled }) {
  const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(1, "year").startOf("day"));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf("day"));
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [galleryPage, setGalleryPage] = useState(0);
  const [galleryRowsPerPage, setGalleryRowsPerPage] = useState(10);
  const [selectedPriceHistoryId, setSelectedPriceHistoryId] = useState(null);

  const startDateForApi = dateFrom && dateFrom.isValid() ? dateFrom.format("YYYY-MM-DD") : "";
  const endDateForApi = dateTo && dateTo.isValid() ? dateTo.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : "";

  const flyerReportQueriesReady = Boolean(
    queriesEnabled &&
      startDateForApi &&
      endDateForApi &&
      dateFrom?.isValid() &&
      dateTo?.isValid() &&
      !dateFrom.startOf("day").isAfter(dateTo.endOf("day"))
  );

  const flyerFilterOptionsSwrKey =
    queriesEnabled && flyerReportQueriesReady
      ? ["flyer-filter-options", startDateForApi, endDateForApi]
      : null;

  const { data: flyerDistinctFilterOptions, error: filterOptionsError } = useSWR(
    flyerFilterOptionsSwrKey,
    () =>
      fetchFlyerReportDistinctFilterOptions({
        dateFrom: startDateForApi,
        dateTo: endDateForApi,
      }),
    flyerReportSwrOptions
  );

  const provinceCodeToBeefAiProvinceString = useMemo(() => {
    const provinceCodeToRawString = new Map();
    for (const row of flyerDistinctFilterOptions?.provinces || []) {
      const rawProvince = row.province;
      const provinceCode = toProv(rawProvince);
      if (provinceCode && rawProvince != null && String(rawProvince).trim() && !provinceCodeToRawString.has(provinceCode)) {
        provinceCodeToRawString.set(provinceCode, String(rawProvince).trim());
      }
    }
    return provinceCodeToRawString;
  }, [flyerDistinctFilterOptions]);

  const provinceChoices = useMemo(() => {
    const provinceCodes = [
      ...new Set(
        (flyerDistinctFilterOptions?.provinces || [])
          .map((row) => toProv(row.province))
          .filter(Boolean)
      ),
    ];
    return getProvinceOptionList(provinceCodes);
  }, [flyerDistinctFilterOptions]);

  const provinceFilterForApi = useMemo(() => {
    if (selectedProvince == null) return undefined;
    return (
      provinceCodeToBeefAiProvinceString.get(selectedProvince.value) ?? selectedProvince.value
    );
  }, [selectedProvince, provinceCodeToBeefAiProvinceString]);

  const retailerChoices = useMemo(() => {
    const retailerNames = (flyerDistinctFilterOptions?.retailers || [])
      .map((row) => row.retailer)
      .filter((name) => name != null && String(name).trim());
    return [...new Set(retailerNames)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [flyerDistinctFilterOptions]);

  const flyerProductFilterChoicesFromApi = useMemo(() => {
    const choices = (flyerDistinctFilterOptions?.products || [])
      .map((row) => {
        const productId = String(row.product_id ?? "").trim();
        if (!productId) return null;
        const productName = String(row.product_name ?? "").trim();
        return {
          id: productId,
          name: productName || productId,
        };
      })
      .filter(Boolean);
    return choices.sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" })
    );
  }, [flyerDistinctFilterOptions]);

  const productIdsNeedingCatalogNames = useMemo(
    () =>
      flyerProductFilterChoicesFromApi
        .filter((product) => product.name === product.id)
        .map((product) => product.id),
    [flyerProductFilterChoicesFromApi]
  );

  const flyerProductNamesSwrKey =
    productIdsNeedingCatalogNames.length > 0
      ? ["flyer-product-filter-names", productIdsNeedingCatalogNames.join(",")]
      : null;

  const { data: catalogProductsForFilterChoices } = useSWR(
    flyerProductNamesSwrKey,
    () => fetchProductsByIds(productIdsNeedingCatalogNames),
    flyerReportSwrOptions
  );

  const flyerProductFilterChoices = useMemo(() => {
    const catalogNameById = new Map();
    for (const catalogProduct of catalogProductsForFilterChoices || []) {
      if (catalogProduct?.id) {
        catalogNameById.set(String(catalogProduct.id), catalogProduct.name || String(catalogProduct.id));
      }
    }
    return flyerProductFilterChoicesFromApi.map((product) => ({
      id: product.id,
      name: catalogNameById.get(product.id) || product.name,
    }));
  }, [flyerProductFilterChoicesFromApi, catalogProductsForFilterChoices]);

  const flyerReportQueryFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      province: provinceFilterForApi,
      retailer: selectedRetailer || undefined,
      productId: selectedProduct?.id || undefined,
      unitType: "weight",
    }),
    [
      startDateForApi,
      endDateForApi,
      provinceFilterForApi,
      selectedRetailer,
      selectedProduct?.id,
    ]
  );

  const flyerSummaryStatsSwrKey =
    queriesEnabled && flyerReportQueriesReady
      ? ["flyer-summary-stats", flyerReportQueryFilters]
      : null;

  const {
    data: flyerSummaryStats,
    error: summaryStatsError,
    isLoading: isSummaryStatsLoading,
  } = useSWR(
    flyerSummaryStatsSwrKey,
    () => fetchFlyerReportSummaryStats(flyerReportQueryFilters),
    flyerReportSwrOptions
  );

  const flyerRetailerChartSwrKey =
    queriesEnabled && flyerReportQueriesReady && !selectedRetailer
      ? ["flyer-chart-retailer", flyerReportQueryFilters]
      : null;

  const flyerFrequencyChartSwrKey =
    queriesEnabled && flyerReportQueriesReady
      ? ["flyer-chart-frequency", flyerReportQueryFilters]
      : null;

  const flyerProvinceChartSwrKey =
    queriesEnabled && flyerReportQueriesReady && selectedProvince == null
      ? ["flyer-chart-province", flyerReportQueryFilters]
      : null;

  const {
    data: retailerDiscountTypeRows,
    error: retailerChartError,
    isLoading: isRetailerChartLoading,
  } = useSWR(
    flyerRetailerChartSwrKey,
    () => fetchFlyerOfferCountByRetailerAndDiscountType(flyerReportQueryFilters),
    flyerReportSwrOptions
  );

  const {
    data: promotionalFrequencyRows,
    error: frequencyChartError,
    isLoading: isFrequencyChartLoading,
  } = useSWR(
    flyerFrequencyChartSwrKey,
    () => fetchFlyerPromotionalFrequency(flyerReportQueryFilters),
    flyerReportSwrOptions
  );

  const {
    data: provinceOfferCountRows,
    error: provinceChartError,
    isLoading: isProvinceChartLoading,
  } = useSWR(
    flyerProvinceChartSwrKey,
    () => fetchFlyerOfferCountByProvince(flyerReportQueryFilters),
    flyerReportSwrOptions
  );

  const flyerGalleryPageSwrKey =
    queriesEnabled && flyerReportQueriesReady
      ? ["flyer-gallery-page", flyerReportQueryFilters, galleryPage, galleryRowsPerPage]
      : null;

  const {
    data: galleryPagePayload,
    error: galleryFetchError,
    isLoading: isGalleryLoading,
  } = useSWR(
    flyerGalleryPageSwrKey,
    () =>
      fetchFlyerGalleryPage(flyerReportQueryFilters, {
        page: galleryPage + 1,
        limit: galleryRowsPerPage,
      }),
    flyerReportSwrOptions
  );

  const galleryDisplayItems = useMemo(
    () => mapFlyerGalleryRowsToDisplayItems(galleryPagePayload?.rows ?? []),
    [galleryPagePayload]
  );

  const galleryTotalCount = galleryPagePayload?.totalCount ?? 0;
  const isGalleryRefreshing = isGalleryLoading && galleryPagePayload !== undefined;
  const isGalleryInitialLoading = isGalleryLoading && galleryPagePayload === undefined;

  const selectedGalleryDisplayItem = useMemo(
    () => galleryDisplayItems.find((item) => item.id === selectedPriceHistoryId) ?? null,
    [galleryDisplayItems, selectedPriceHistoryId]
  );

  const handleGalleryPageChange = useCallback((_, nextPage) => {
    setGalleryPage(nextPage);
  }, []);

  const handleGalleryRowsPerPageChange = useCallback((event) => {
    setGalleryRowsPerPage(parseInt(event.target.value, 10));
    setGalleryPage(0);
  }, []);

  const handleGalleryCardClick = useCallback((displayItem) => {
    if (displayItem?.id) {
      setSelectedPriceHistoryId(displayItem.id);
    }
  }, []);

  const handleCloseFlyerGalleryDetail = useCallback(() => {
    setSelectedPriceHistoryId(null);
  }, []);

  const galleryServerPagination = useMemo(
    () => ({
      page: galleryPage,
      rowsPerPage: galleryRowsPerPage,
      totalCount: galleryTotalCount,
      onPageChange: handleGalleryPageChange,
      onRowsPerPageChange: handleGalleryRowsPerPageChange,
    }),
    [
      galleryPage,
      galleryRowsPerPage,
      galleryTotalCount,
      handleGalleryPageChange,
      handleGalleryRowsPerPageChange,
    ]
  );

  const retailerStackedBarChart = useMemo(
    () => buildFlyerRetailerStackedBarChart(retailerDiscountTypeRows ?? []),
    [retailerDiscountTypeRows]
  );

  const promotionalFrequencyLineChart = useMemo(
    () => buildFlyerPromotionalFrequencyLineChart(promotionalFrequencyRows ?? []),
    [promotionalFrequencyRows]
  );

  const provinceOfferCountChart = useMemo(
    () => buildFlyerProvinceOfferCountChart(provinceOfferCountRows ?? []),
    [provinceOfferCountRows]
  );

  const anyFlyerReportFetchError = filterOptionsError || summaryStatsError;

  useEffect(() => {
    if (
      selectedProvince &&
      provinceChoices.length > 0 &&
      !provinceChoices.some((option) => option.value === selectedProvince.value)
    ) {
      setSelectedProvince(null);
    }
  }, [selectedProvince, provinceChoices]);

  useEffect(() => {
    if (selectedRetailer && retailerChoices.length > 0 && !retailerChoices.includes(selectedRetailer)) {
      setSelectedRetailer(null);
    }
  }, [selectedRetailer, retailerChoices]);

  useEffect(() => {
    if (
      selectedProduct?.id &&
      flyerProductFilterChoices.length > 0 &&
      !flyerProductFilterChoices.some((product) => product.id === selectedProduct.id)
    ) {
      setSelectedProduct(null);
    }
  }, [selectedProduct, flyerProductFilterChoices]);

  useEffect(() => {
    setGalleryPage(0);
  }, [flyerReportQueryFilters]);

  useEffect(() => {
    setSelectedPriceHistoryId(null);
  }, [flyerReportQueryFilters, galleryPage, galleryRowsPerPage]);

  return {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    selectedProvince,
    setSelectedProvince,
    selectedRetailer,
    setSelectedRetailer,
    selectedProduct,
    setSelectedProduct,
    startDateForApi,
    endDateForApi,
    flyerReportQueriesReady,
    flyerReportQueryFilters,
    flyerFilterOptionsSwrKey,
    filterOptionsError,
    anyFlyerReportFetchError,
    provinceChoices,
    provinceFilterForApi,
    retailerChoices,
    flyerProductFilterChoices,
    flyerSummaryStatsSwrKey,
    flyerRetailerChartSwrKey,
    flyerFrequencyChartSwrKey,
    flyerProvinceChartSwrKey,
    isFilterOptionsLoading: Boolean(flyerFilterOptionsSwrKey) && !flyerDistinctFilterOptions && !filterOptionsError,
    isProductFilterChoicesLoading:
      Boolean(flyerProductNamesSwrKey) && !catalogProductsForFilterChoices && !filterOptionsError,
    flyerSummaryStats,
    summaryStatsError,
    isSummaryStatsLoading,
    retailerStackedBarChart,
    promotionalFrequencyLineChart,
    provinceOfferCountChart,
    retailerChartError,
    frequencyChartError,
    provinceChartError,
    isRetailerChartLoading,
    isFrequencyChartLoading,
    isProvinceChartLoading,
    flyerGalleryPageSwrKey,
    galleryDisplayItems,
    galleryFetchError,
    isGalleryInitialLoading,
    isGalleryRefreshing,
    galleryServerPagination,
    selectedPriceHistoryId,
    selectedGalleryDisplayItem,
    handleGalleryCardClick,
    handleCloseFlyerGalleryDetail,
  };
}
